import { useState } from 'react';
import { Badge, Button, Card, copyText, type CopyState } from '@zarya/ui';
import { contractInText, type Contract, type ContractView } from './ContractsModel';
import s from './Contracts.module.css';
import { t } from '@app/i18n';

export interface ContractTextCardProps {
  view: ContractView;
  /** Versão exibida. `null` = o vigente do `view`. */
  contrato: Contract | null;
  /** `true` quando o que está na tela não é o vigente (veio do card Releases). */
  historica: boolean;
  onVerVigente: () => void;
}

/**
 * Card **Contrato vigente v{N}** (FR-069, FR-070): o artefato inteiro em texto,
 * copiável. É o que o cliente cola numa auditoria — por isso é texto, não tabela.
 */
export function ContractTextCard({
  view,
  contrato,
  historica,
  onVerVigente,
}: ContractTextCardProps) {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const texto = contractInText(view, contrato);
  const hasContract = Boolean(contrato);

  const copy = () => {
    void copyText(texto).then((ok) => setCopyState(ok ? 'done' : 'failed'));
  };

  return (
    <Card
      title={
        <>
          {historica
            ? t('contracts.text.historic', { version: contrato?.version })
            : t('contracts.text.current')}
          {hasContract ? (
            <Badge tone={historica ? 'neutral' : 'success'}>
              {historica
                ? t('contracts.text.badgeReplaced', { version: contrato?.version })
                : t('contracts.text.badgeVersion', { version: contrato?.version })}
            </Badge>
          ) : (
            <Badge tone="danger">{t('contracts.text.noContract')}</Badge>
          )}
        </>
      }
      toolbar={
        <>
          {historica ? (
            <Button variant="out" size="sm" icon="arrow-right" onClick={onVerVigente}>
              {t('contracts.text.seeCurrent')}
            </Button>
          ) : null}
          <Button variant="out" size="sm" icon="csv" onClick={copy} disabled={!hasContract}>
            {copyState === 'failed'
              ? t('common.copyFailed')
              : t(copyState === 'done' ? 'contracts.text.copied' : 'contracts.text.copy')}
          </Button>
        </>
      }
    >
      <pre className={s.contrato} data-testid="contrato-texto">
        {texto}
      </pre>
    </Card>
  );
}
